import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthFacade } from '../../auth/auth-facade';

/**
 * Session Expiry Interceptor
 *
 * Zero Trust requirement: a 401 response from the API means the current
 * token is no longer valid server-side (expired, revoked, or rotated).
 * The session must be terminated immediately — the client cannot be trusted
 * to continue operating with an invalid token.
 *
 * Behaviour:
 * - Passes non-401 errors through unchanged.
 * - On 401, triggers logout only if the user is currently authenticated to
 *   prevent redirect loops on the sign-in page or during the login flow.
 * - Navigates to /signin with reason=session_expired so the UI can display
 *   an informational message.
 * - Always re-throws the original error so calling code is not silently swallowed.
 *
 * OIDC note: when the real Spring Boot OIDC AuthFacade is in place, logout()
 * will redirect to the OIDC end_session_endpoint and this interceptor will
 * continue to work without changes — the navigation below will be superseded
 * by the OIDC logout redirect.
 */
export const sessionExpiryInterceptor: HttpInterceptorFn = (request, next) => {
  const authFacade = inject(AuthFacade);
  const router = inject(Router);

  return next(request).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {

        // Only react when authenticated to avoid redirect loops on the
        // sign-in page or during the initial OIDC callback.
        if (!authFacade.user()) {
          const navigateToSignIn = (): void => {
            void router.navigate(['/signin'], {
              queryParams: { reason: 'session_expired' },
            });
          };

          authFacade.logout().subscribe({
            next: navigateToSignIn,
            error: navigateToSignIn,
          });
        }
      }

      return throwError(() => error);
    })
  );
};
